import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	ICreateSiteThunkArgs,
	IDeleteSiteThunkArgs,
	IGetSiteDetailsThunkArgs,
	IGetSitesResponse,
	IGetSitesThunkArgs,
	ISite,
	ISiteDetails,
	IUpdateSiteThunkArgs,
} from 'src/features/site/domain/types';
import { siteApi } from 'src/features/site/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const SITE_LOADING_KEY = 'site';

export const getSites = createAsyncThunk<
	IGetSitesResponse,
	IGetSitesThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site/getSites',
	async ({ request, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: true }));
			const response = await siteApi.getSites(request);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch sites');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: false }));
		}
	},
);

export const createSite = createAsyncThunk<
	ISite,
	ICreateSiteThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site/createSite',
	async ({ request, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: true }));
			const response = await siteApi.createSite(request);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create site');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: false }));
		}
	},
);

export const getSiteDetails = createAsyncThunk<
	ISiteDetails,
	IGetSiteDetailsThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site/getSiteDetails',
	async ({ siteSlug, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: true }));
			const response = await siteApi.getSiteDetails(siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch site details');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: false }));
		}
	},
);

export const updateSite = createAsyncThunk<
	ISite,
	IUpdateSiteThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site/updateSite',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: true }));
			const response = await siteApi.updateSite(request, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update site');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: false }));
		}
	},
);

export const deleteSite = createAsyncThunk<
	void,
	IDeleteSiteThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'site/deleteSite',
	async ({ siteSlug, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: true }));
			const response = await siteApi.deleteSite(siteSlug);

			onSuccess?.();
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to delete site');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: SITE_LOADING_KEY, loading: false }));
		}
	},
);
